import React, { useEffect, useRef, useState } from 'react';
import { onboardingAPI } from '../../utils/api';
import {
  Users, Phone, UserCheck, IdCard, ShieldCheck, FileSignature,
  AlertTriangle, Bell, RefreshCw, Power, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter431 — Admin talent onboarding compliance dashboard.
 *
 * Live funnel of where talent are stuck (phone / profile / ID / IDV / contract),
 * the booked-but-incomplete follow-up list, a one-click reminder nudge, and the
 * master switch for the HARD onboarding gate (kept OFF until the owner
 * authorizes activation once production SMS is confirmed working).
 */

const FunnelCard = ({ icon: Icon, label, value, total, tone = 'slate' }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const toneMap = {
    slate: 'text-slate-600 bg-slate-100',
    cyan: 'text-cyan-700 bg-cyan-100',
    emerald: 'text-emerald-700 bg-emerald-100',
    amber: 'text-amber-700 bg-amber-100',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value?.toLocaleString?.() ?? value}</p>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      {total ? <p className="mt-0.5 text-[11px] text-slate-400">{pct}% of active talent</p> : null}
    </div>
  );
};

const StuckBar = ({ label, value, total, tone }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const barTone = {
    rose: 'bg-rose-500', amber: 'bg-amber-500', cyan: 'bg-cyan-500', violet: 'bg-violet-500', slate: 'bg-slate-500',
  }[tone] || 'bg-slate-500';
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="font-bold text-slate-900">{value?.toLocaleString?.() ?? value}</span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function TalentOnboardingCompliance() {
  const [overview, setOverview] = useState(null);
  const [booked, setBooked] = useState(null);
  const [gateEnabled, setGateEnabled] = useState(false);
  const [phoneGateEnabled, setPhoneGateEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gateBusy, setGateBusy] = useState(false);
  const [phoneGateBusy, setPhoneGateBusy] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const loadIdRef = useRef(0);

  const load = async () => {
    const myId = ++loadIdRef.current;
    setLoading(true);
    try {
      const [o, b, pg] = await Promise.all([
        onboardingAPI.adminOverview(),
        onboardingAPI.adminBookedIncomplete(),
        onboardingAPI.adminGetPhoneGate(),
      ]);
      if (myId !== loadIdRef.current) return;  // a newer load / toggle superseded this one
      setOverview(o.data);
      setBooked(b.data);
      setGateEnabled(!!o.data.gate_enabled);
      setPhoneGateEnabled(!!pg.data.enabled);
    } catch (e) {
      if (myId === loadIdRef.current) toast.error('Failed to load compliance data');
    } finally {
      if (myId === loadIdRef.current) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePhoneGate = async () => {
    const next = !phoneGateEnabled;
    const msg = next
      ? 'Turn mandatory SMS phone verification BACK ON? Talent who have not verified a phone number will be blocked from the portal (and from clocking in) until they verify. Do this only after production SMS is confirmed working.'
      : 'Turn OFF mandatory SMS phone verification? Talent can log in and clock in without verifying a phone number. (Use this during live events if SMS is failing.)';
    if (!window.confirm(msg)) return;
    setPhoneGateBusy(true);
    try {
      const res = await onboardingAPI.adminSetPhoneGate(next);
      loadIdRef.current++;  // discard any in-flight load() so it can't overwrite the toggle
      setPhoneGateEnabled(!!res.data.enabled);
      toast.success(res.data.enabled ? 'Mandatory SMS verification ON' : 'Mandatory SMS verification OFF');
    } catch (e) {
      toast.error('Failed to change SMS verification');
    } finally {
      setPhoneGateBusy(false);
    }
  };

  const toggleGate = async () => {
    const next = !gateEnabled;
    const msg = next
      ? 'Activate the HARD onboarding gate? Incomplete talent will be restricted to onboarding screens only. Do this only after production SMS is confirmed.'
      : 'Deactivate the hard onboarding gate? Talent will regain normal access.';
    if (!window.confirm(msg)) return;
    setGateBusy(true);
    try {
      const res = await onboardingAPI.adminSetGate(next);
      loadIdRef.current++;  // discard any in-flight load() so it can't overwrite the gate
      setGateEnabled(!!res.data.enabled);
      toast.success(res.data.enabled ? 'Hard gate ACTIVATED' : 'Hard gate deactivated');
    } catch (e) {
      toast.error('Failed to change gate');
    } finally {
      setGateBusy(false);
    }
  };

  const notify = async () => {
    setNotifyBusy(true);
    try {
      const res = await onboardingAPI.adminNotify(true);
      toast.success(`Reminder sent to ${res.data.notified} booked talent`);
    } catch (e) {
      toast.error('Failed to send reminders');
    } finally {
      setNotifyBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600" />
      </div>
    );
  }

  const total = overview?.total || 0;

  return (
    <div data-testid="admin-onboarding-compliance" className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
            Talent Onboarding Compliance
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Live view of where talent stall across the verification sequence.
          </p>
        </div>
        <button
          data-testid="compliance-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* iter432 — Mandatory SMS phone-verification live status + kill-switch */}
      <div
        data-testid="compliance-phone-gate-panel"
        className={`mb-4 rounded-2xl border p-4 ${phoneGateEnabled ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <MessageSquare className={`h-5 w-5 ${phoneGateEnabled ? 'text-emerald-600' : 'text-rose-600'}`} />
            <div>
              <p className="text-sm font-bold text-slate-900" data-testid="phone-gate-status">
                Mandatory SMS phone verification: {phoneGateEnabled ? 'ON' : 'OFF'}
              </p>
              <p className="text-xs text-slate-600 max-w-xl">
                {phoneGateEnabled
                  ? 'Talent must verify a phone number before they can log in or clock in. Clients & agencies are exempt.'
                  : 'SMS verification is NOT required — booked talent can log in and clock in normally. Turn back ON only after production SMS is confirmed working.'}
              </p>
            </div>
          </div>
          <button
            data-testid="compliance-phone-gate-toggle"
            onClick={togglePhoneGate}
            disabled={phoneGateBusy}
            className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              phoneGateEnabled ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {phoneGateEnabled ? 'Turn OFF SMS verification' : 'Turn ON SMS verification'}
          </button>
        </div>
      </div>

      {/* Gate master switch */}
      <div
        data-testid="compliance-gate-panel"
        className={`mb-6 rounded-2xl border p-4 ${gateEnabled ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Power className={`h-5 w-5 ${gateEnabled ? 'text-emerald-600' : 'text-amber-600'}`} />
            <div>
              <p className="text-sm font-bold text-slate-900">
                Hard onboarding gate: {gateEnabled ? 'ACTIVE' : 'Disabled'}
              </p>
              <p className="text-xs text-slate-600 max-w-xl">
                {gateEnabled
                  ? 'Incomplete talent are restricted to onboarding screens only (server + client enforced).'
                  : 'Enforcement is OFF. Build is complete — enable only after production SMS is confirmed working.'}
              </p>
            </div>
          </div>
          <button
            data-testid="compliance-gate-toggle"
            onClick={toggleGate}
            disabled={gateBusy}
            className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              gateEnabled ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            {gateEnabled ? 'Deactivate gate' : 'Activate gate'}
          </button>
        </div>
      </div>

      {/* Funnel */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <FunnelCard icon={Users} label="Active talent" value={total} />
        <FunnelCard icon={Phone} label="Phone verified" value={overview?.phone_ok} total={total} tone="cyan" />
        <FunnelCard icon={UserCheck} label="Profile complete" value={overview?.profile_ok} total={total} tone="cyan" />
        <FunnelCard icon={IdCard} label="ID uploaded" value={overview?.id_ok} total={total} tone="cyan" />
        <FunnelCard icon={ShieldCheck} label="IDV approved" value={overview?.idv_ok} total={total} tone="amber" />
        <FunnelCard icon={FileSignature} label="Fully complete" value={overview?.complete} total={total} tone="emerald" />
      </div>

      {/* Stuck breakdown */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6">
        <p className="text-sm font-bold text-slate-900 mb-4">Where talent are stuck</p>
        <div className="space-y-3">
          <StuckBar label="Stuck at phone verification" value={overview?.stuck_phone} total={total} tone="rose" />
          <StuckBar label="Phone done, profile incomplete" value={overview?.stuck_profile} total={total} tone="amber" />
          <StuckBar label="Profile done, ID not uploaded" value={overview?.stuck_id_upload} total={total} tone="amber" />
          <StuckBar label="ID uploaded, IDV not approved" value={overview?.stuck_idv} total={total} tone="cyan" />
          <StuckBar label="IDV approved, agreement unsigned" value={overview?.stuck_agreement} total={total} tone="violet" />
        </div>
      </div>

      {/* Booked but incomplete */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <p className="text-sm font-bold text-slate-900">
              Booked but not onboarded ({booked?.count ?? 0})
            </p>
          </div>
          <button
            data-testid="compliance-notify"
            onClick={notify}
            disabled={notifyBusy || !(booked?.count)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-700 disabled:opacity-60"
          >
            <Bell className="h-3.5 w-3.5" /> Nudge all
          </button>
        </div>
        {booked?.count ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-3">Talent</th>
                  <th className="py-2 pr-3">Next event</th>
                  <th className="py-2 pr-3">Stuck at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {booked.talent.map((t) => (
                  <tr key={t.user_id} data-testid={`booked-incomplete-${t.user_id}`}>
                    <td className="py-2 pr-3">
                      <p className="font-medium text-slate-900">{t.name}</p>
                      <p className="text-xs text-slate-400">{t.email}</p>
                    </td>
                    <td className="py-2 pr-3 text-slate-600">{t.next_shift_date || '—'}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                        {t.current_step_label || t.current_step}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No booked talent with incomplete onboarding. 🎉</p>
        )}
      </div>
    </div>
  );
}
